/** XeAgents.ai**/
import { Link } from "wouter";
import { ChevronRight } from "lucide-react";
import { SaveTemplateButton } from "./SaveTemplateButton";

/**
 * Breadcrumb, title, and the single action.
 *
 * Sticky, because Save is the only thing you can do here and on a four-step form
 * it would otherwise scroll a long way out of reach. Nothing else lives up here:
 * a second button beside it would read as an equal choice, and there isn't one.
 */
export function TemplateHeader({
  templateName,
  onSave,
  canSave,
  saving,
  saved,
  invalidCount,
}: {
  templateName: string;
  onSave: () => void;
  canSave: boolean;
  saving: boolean;
  saved: boolean;
  invalidCount: number;
}) {
  return (
    <header className="sticky top-0 z-20 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/75">
      <div className="mx-auto w-full max-w-[1600px] px-4 py-4 sm:px-6 lg:px-8">
        <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-xs text-muted-foreground">
          <Link href="/app/flows/templates" className="hover:text-foreground hover:underline">
            Templates
          </Link>
          <ChevronRight className="h-3 w-3" aria-hidden="true" />
          <span className="max-w-[12rem] truncate sm:max-w-none">{templateName}</span>
          <ChevronRight className="h-3 w-3" aria-hidden="true" />
          <span className="font-medium text-foreground">Customize</span>
        </nav>

        <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold tracking-tight sm:text-2xl" data-testid="text-page-title">
              Customize {templateName} Template
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              Create a high-converting AI sales script tailored to your business, product and audience.
            </p>
          </div>

          {/* Hidden on mobile: there it lives in the sticky bar at the bottom,
              where a thumb is, rather than above the fold it scrolled past. */}
          <div className="hidden shrink-0 flex-col items-end gap-1 sm:flex">
            <SaveTemplateButton onSave={onSave} disabled={!canSave} saving={saving} saved={saved} size="lg" />
            {!canSave && !saving && invalidCount > 0 && (
              <p className="text-[11px] text-muted-foreground">
                {invalidCount} required field{invalidCount === 1 ? "" : "s"} still to fill
              </p>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
